import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InquiryForm() {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    principal_name: "", designation: "", school_name: "", city: "",
    contact_number: "", email: "", program_interest: "",
    preferred_date: "", num_students: "", grade_levels: "", special_requirements: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add("visible"); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.principal_name || !form.school_name || !form.email || !form.contact_number || !form.program_interest) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Enquiry submitted successfully! We'll respond within 2 hours.");
      setForm({
        principal_name: "", designation: "", school_name: "", city: "",
        contact_number: "", email: "", program_interest: "",
        preferred_date: "", num_students: "", grade_levels: "", special_requirements: "",
      });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="inquiry" data-testid="inquiry-section" ref={ref} className="reveal-section py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Get Started</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-foreground mb-6">
              Let Us Build Something Unforgettable
            </h2>
            <p className="text-base text-muted-foreground font-light leading-relaxed mb-8">
              Give your students the opportunity to experience, explore, and excel beyond the classroom. Submit your enquiry and our team will respond within 2 hours.
            </p>
            <div className="flex items-center gap-3 p-4 bg-accent/10 border border-accent/20 rounded-sm mb-6">
              <Clock className="w-5 h-5 text-accent shrink-0" />
              <p className="text-sm text-foreground font-medium">We respond within 2 hours</p>
            </div>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p><span className="font-medium text-foreground">Email:</span> info@osaacademy.in</p>
              <p><span className="font-medium text-foreground">Phone:</span> +91 8618396055</p>
              <p><span className="font-medium text-foreground">Alt Phone:</span> +91 8147068989 / +91 8147071616</p>
              <p><span className="font-medium text-foreground">Location:</span> A short drive from Bengaluru</p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="inquiry-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Name of Principal / Director *</label>
                  <Input data-testid="inquiry-principal-name" placeholder="Full name" value={form.principal_name}
                    onChange={(e) => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setForm(p => ({...p, principal_name: v})); }}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Designation</label>
                  <Input data-testid="inquiry-designation" placeholder="e.g. Principal, Trustee, Coordinator" value={form.designation}
                    onChange={(e) => setForm(p => ({...p, designation: e.target.value}))}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">School / Institution *</label>
                  <Input data-testid="inquiry-school-name" placeholder="School name" value={form.school_name}
                    onChange={(e) => { const v = e.target.value.replace(/[^a-zA-Z0-9\s.'-]/g, ''); setForm(p => ({...p, school_name: v})); }}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">City & District</label>
                  <Input data-testid="inquiry-city" placeholder="e.g. Bengaluru, Mysuru" value={form.city}
                    onChange={(e) => setForm(p => ({...p, city: e.target.value}))}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Contact Number *</label>
                  <Input data-testid="inquiry-contact" type="tel" placeholder="+91 XXXXX XXXXX" value={form.contact_number}
                    onChange={(e) => { const v = e.target.value.replace(/[^0-9+\s-]/g, ''); setForm(p => ({...p, contact_number: v})); }}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Email Address *</label>
                  <Input data-testid="inquiry-email" type="email" placeholder="email@school.com" value={form.email}
                    onChange={(e) => setForm(p => ({...p, email: e.target.value}))}
                    className="bg-card border-border/60 focus:border-primary" required />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1.5 block">Program of Interest *</label>
                <Input data-testid="inquiry-program" placeholder="e.g. AgriLearn Campus, AeroLab Experience, SailScience Program" value={form.program_interest}
                  onChange={(e) => setForm(p => ({...p, program_interest: e.target.value}))}
                  className="bg-card border-border/60 focus:border-primary" />
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Preferred Date</label>
                  <Input data-testid="inquiry-date" type="month" value={form.preferred_date}
                    onChange={(e) => setForm(p => ({...p, preferred_date: e.target.value}))}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Number of Students</label>
                  <Input data-testid="inquiry-students" placeholder="e.g. 120" value={form.num_students}
                    onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setForm(p => ({...p, num_students: v})); }}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Grade Levels</label>
                  <Input data-testid="inquiry-grades" placeholder="e.g. Grade 5-8" value={form.grade_levels}
                    onChange={(e) => setForm(p => ({...p, grade_levels: e.target.value}))}
                    className="bg-card border-border/60 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1.5 block">Special Requirements or Notes</label>
                <Textarea data-testid="inquiry-notes" placeholder="Any special requirements, dietary needs, accessibility considerations..."
                  value={form.special_requirements} onChange={(e) => setForm(p => ({...p, special_requirements: e.target.value}))}
                  className="bg-card border-border/60 focus:border-primary min-h-[100px]" />
              </div>
              <button type="submit" data-testid="inquiry-submit-btn" disabled={loading}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                <Send className="w-4 h-4" />
                {loading ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
